import React, { useState, useEffect, useMemo } from "react";
import { Html5QrcodeScanType, Html5Qrcode } from "html5-qrcode"
import { BasicFetch } from '../../../utils/BasicFetch.ts';
import { formatDate } from '../../utilities/FormatDate.ts';
import { CollectionReptile } from '../types/CollectionTypes.ts';
import { ReptoolModal } from '../../utilities/ReptoolModal.tsx';
import { ReptileTile } from "../../utilities/ReptileTile.tsx";
import { Button, Table } from "react-bootstrap";
import { Carousel } from 'react-responsive-carousel';
import { faPoop, faUtensils, faBroom, faWorm, faArrowRight, faArrowLeft, faBarcode, faMobile } from "@fortawesome/free-solid-svg-icons";
import { CareInfoRow } from './ReptileCareInfoModal.tsx'
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { driver } from 'driver.js';
import { LabelModal } from './LabelModal.tsx';
import { Reptile } from "../../../types/Reptile.ts";
import { CardSelect } from "../../utilities/shared_components/CardSelect.tsx";
import { useRepToolContext } from "../../../RepToolContext.tsx";

export const ScanModal = ({ closeModal, reptiles }: { closeModal: () => void, reptiles: Reptile[] }) => {
  const [scanning, setScanning] = useState(false);
  const [recordsCreated, setRecordsCreated] = useState(false);
  const [scannedId, setScannedId] = useState(null);
  const [qrScanner, setQrScanner] = useState<Html5Qrcode | null>(null);
  const [currentReptile, setCurrentReptile] = useState<CollectionReptile | null>(null);
  const [createFeed, setCreateFeed] = useState(false);
  const [createClean, setCreateClean] = useState(false);
  const [createShed, setCreateShed] = useState(false);
  const [createDef, setCreateDef] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const [decodedText, setDecodedText] = useState<string>("");
  const [scannedText, setScannedText] = useState<string>('');
  const [showLabelModal, setShowLabelModal] = useState(false);
  const { appendControls } = useRepToolContext();
  const [scanMethod, setScanMethod] = useState<"camera" | "barcode" | null>(null);
  const driverObjScan = useMemo(() => {
    if (activeIndex === 0) {
      return driver({
        steps: [
          {
            element: '#record-select',
            popover: {
              title: 'Select Records',
              description: 'Select the records you would like to create in the next step',
            }
          },
          {
            element: '#create-labels-scan-btn',
            popover: {
              title: 'Create Labels',
              description: 'Haven\'t created labels yet? Click here to create labels for your reptiles.',
            }
          },
          {
            element: '#start-scan-btn',
            popover: {
              title: 'Start Scanning',
              description: 'Click here to start scanning QR codes',
            }
          }
        ]
      });
    } else if (activeIndex === 1) {

    }
  }, [activeIndex])

  const stopAndCloseModal = () => {
    if (scanning) {
      qrScanner?.stop();
    }
    closeModal();
  }
  const scannerConfig = {
    fps: 10,
    supportedScanTypes: [Html5QrcodeScanType.SCAN_TYPE_CAMERA],
    rememberLastUsedCamera: true,
    willReadFrequently: true,
    focusMode: "continuous",
    aspectRatio: 1.777778,

    qrbox: { width: 100, height: 100 },
  }

  const onChange = (id: number, description: string, date: string) => {
    if (!currentReptile) {
      return
    }
    const data = new FormData();
    data.append("date", formatDate(date).toUTCString());
    data.append("description", description);

    BasicFetch({
      url: `${(window as any).serverLocation}collection_reptile/${currentReptile.id}/care_info/${id}/edit`,
      data,
      method: 'POST',
      contentType: 'multipart/form-data',
      onSuccess: (data: { reptile: CollectionReptile }) => {
        setCurrentReptile({ ...data.reptile });
      },
      onFail: () => {
        (window as any).alert("There was an error saving changes, please try again later");
      },
    })
  }


  useEffect(() => {
    if (qrScanner == null) {
      return
    }
    if (scanning) {
      function onScanSuccess(decodedText, decodedResult) {
        // handle the scanned code as you like, for example:
        console.log(`Code matched = ${decodedText}`, decodedResult);

        setRecordsCreated(false);
        setDecodedText(decodedText);

      }

      function onScanFailure(error) {
        // handle scan failure, usually better to ignore and keep scanning.
        // for example:
        //console.warn(`Code scan error = ${error}`);
      }

      qrScanner.start({ facingMode: "environment" }, scannerConfig, onScanSuccess, onScanFailure);
    } else {
      qrScanner.stop();
    }

  }, [scanning]);
  useEffect(() => {
    if (activeIndex === 2 && scanMethod === 'camera') {
      setQrScanner(new Html5Qrcode("reader"));
      setScanning(true);
    }
  }, [activeIndex])

  useEffect(() => {
    if (showLabelModal) {
      return;
    }
    const anySelected = createFeed || createClean || createDef || createShed;
    if (activeIndex === 0) {
      window.setControls(null);
    } else if (activeIndex === 2) {
      appendControls(
        <div className="d-flex w-100">
          <Button className='mx-auto' variant="outline-light" onClick={() => {
            setRecordsCreated(true);
            setScanning(false);
            setActiveIndex(1)
          }}><FontAwesomeIcon icon={faArrowLeft} className="mr-2" /> Back </Button>
        </div>
      );
    } else {
      appendControls(
        <div className="d-flex w-100 justify-content-center">
          <Button variant="outline-light" onClick={() => setActiveIndex(0)}>
            <FontAwesomeIcon icon={faArrowLeft} className="mr-2" /> Back
          </Button>
          <Button id='create-labels-scan-btn' className='mx-3' variant="outline-light" onClick={() => setShowLabelModal(true)}>Create Labels</Button>
          <Button id='start-scan-btn' variant={`outline-${anySelected ? 'light' : 'secondary'}`} onClick={() => {
            if (!anySelected) {
              alert("Please select at least one record to start scanning");
              return;
            }
            setActiveIndex(2)
          }}> Start Scanning <FontAwesomeIcon icon={faArrowRight} className="ml-2" /></Button>
        </div>
      );
    }
  }, [activeIndex, createFeed, createClean, createDef, createShed, showLabelModal])

  useEffect(() => {
    if (decodedText) {
      fetchReptile(decodedText);
    }
  }, [decodedText])

  const fetchReptile = (text: string) => {
    BasicFetch({
      url: text,
      extra: {
        data_only: 1
      },
      method: 'GET',
      onSuccess: (data) => {
        setScannedId(data.id);
        setScannedText('');
        setDecodedText("");
      },
      onFail: () => alert("Failed to get data"),
    });
  }


  useEffect(() => {
    if (scannedId && !recordsCreated) {
      // create list of records to create
      const recordsToCreate: string[] = [];
      if (createFeed) recordsToCreate.push("FEED");
      if (createClean) recordsToCreate.push("SUBSTRATE");
      if (createDef) recordsToCreate.push("DEFECATION");
      if (createShed) recordsToCreate.push("SHED");

      const data = new FormData();
      data.append("care_types", recordsToCreate.join(","));

      BasicFetch({
        url: `${(window as any).serverLocation}collection_reptile/${scannedId}/care_info/scan_create`,
        contentType: 'multipart/form-data',
        data,
        method: 'POST',
        onSuccess: (data) => {
          setCurrentReptile({ ...data.reptile });
        }
      })
    }
  }, [scannedId])
  return (
    <>
      {showLabelModal && <LabelModal closeModal={() => setShowLabelModal(false)} reptiles={reptiles} />}
      <ReptoolModal
        id={"qrscanner-modal"}
        title={"Scan and Record"}
        closeModal={stopAndCloseModal}
        showTutorial={() => driverObjScan?.drive()}
        body={(
          <Carousel className="text-center" selectedItem={activeIndex} showIndicators={false} showStatus={false} showArrows={false}>
            <div>
              <h3>Scan Method</h3>
              <div className="d-grid-container justify-content-center flex-wrap flex-column flex-md-row col-12 col-md-8 col-lg-6 mx-auto">
                <CardSelect icon={faBarcode} selected={false} text="2D Barcode Scanner" badgeText="Recommended" onSelect={() => {
                  setScanMethod('barcode');
                  setActiveIndex(1);
                }} />
                <CardSelect icon={faMobile} selected={false} text="Mobile Camera" onSelect={() => {
                  setScanMethod('camera');
                  setActiveIndex(1);
                }} />
              </div>
            </div>
            <div className="p-3">
              <h3>What records would you like to create?</h3>
              <div id='record-select' className="d-grid-container col-12 col-md-8 col-lg-6 mx-auto mt-4">
                <CardSelect icon={faUtensils} selected={createFeed} onSelect={() => setCreateFeed(!createFeed)} text="Feeding" />
                <CardSelect icon={faBroom} selected={createClean} onSelect={() => setCreateClean(!createClean)} text="Cleaning" />
                <CardSelect icon={faWorm} selected={createShed} onSelect={() => setCreateShed(!createShed)} text="Shedding" />
                <CardSelect icon={faPoop} selected={createDef} onSelect={() => setCreateDef(!createDef)} text="Defecation" />
              </div>
            </div>
            <div>
              <div id="reader"></div>
              {scanMethod === 'barcode' && <div className="d-flex justify-content-center">
                <p>Start Scanning RepTool QR codes</p>
                <input id='barcode-scan' className="" value={scannedText || ''} type="text" autoComplete={undefined} style={{ height: 0, width: 0, border: 0 }} onChange={(e) => {
                  setScannedText(e.target.value);
                }} onKeyDown={
                  (e) => {
                    if (e.key === 'Enter') {

                      setDecodedText(scannedText);
                    }
                  }
                } />
              </div>}

              {currentReptile && <>
                <div className="mt-3 d-flex justify-content-center">
                  <ReptileTile reptile={currentReptile} hideButton={true} onSelected={() => { }} />
                </div>

                <div className="mt-3 pb-4">
                  <h6>Edit Notes?</h6>
                  <Table variant="dark">
                    <thead>
                      <tr>
                        <th>Date</th>
                        <th>Notes</th>
                        <th>Care Type</th>
                        <th></th>
                      </tr>
                    </thead>
                    <tbody>
                      {createFeed && !!currentReptile.feed_dates?.length && <CareInfoRow
                        onChange={onChange}
                        record={currentReptile.feed_dates[0]}
                        onDelete={() => {
                          setCreateFeed(false);
                        }}
                        addTypeColumn="Feeding"
                      />}
                      {createClean && !!currentReptile.substrate_dates?.length && <CareInfoRow
                        onChange={onChange}
                        record={currentReptile.substrate_dates[0]}
                        onDelete={() => {
                          setCreateClean(false);
                        }}
                        addTypeColumn="Cleaning"
                      />}
                      {createShed && !!currentReptile.shed_dates?.length && <CareInfoRow
                        onChange={onChange}
                        record={currentReptile.shed_dates[0]}
                        onDelete={() => {
                          setCreateShed(false);
                        }}
                        addTypeColumn="Shedding"
                      />}
                      {createDef && !!currentReptile.def_dates?.length && <CareInfoRow
                        onChange={onChange}
                        record={currentReptile.def_dates[0]}
                        onDelete={() => {
                          setCreateDef(false);
                        }}
                        addTypeColumn="Defecation"
                      />}
                    </tbody>
                  </Table>
                </div>
              </>}
            </div>
          </Carousel>
        )} />
    </>

  )
}